import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import { ArrowRight } from 'lucide-react';
import { FS_TYPE_LABELS } from './WorkflowFs';
import {
  fsRequiredGaps, FsSigningDateField, FsAuditOpinionField, FsAuditQuestionFields, FsRemarksField,
} from './WorkflowFsNew';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import EngagementAttachments from '../components/shared/EngagementAttachments';
import { FilePicker, FormModal, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { EngagementName, EngagementNameList, withCoverageFmt } from '../components/workflow/CoverageDisplay';
import BatchSiblings from '../components/workflow/BatchSiblings';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import { useFsAutoDetect } from '../components/workflow/FsDateAutoDetect';
import { FsAiConditionsList, FsAiPageThumbs } from '../components/workflow/FsAiReview';
import { btnApprove, detailLabel, detailValue } from '../components/workflow/constants';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import NowName, { StaffNames } from '../components/shared/NowName';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { engagementHref } from '../utils/engagementLink';

// Signed Financial Statements & QC35 — detail page (docs/modules/workflow-fs.md).
const fmtDate = (v) => (v ? formatDate(v) : '—');
const yesNoDash = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

const CHANGE_LABELS = {
  signingDate: 'Signing Date',
  auditOpinion: 'Audit Opinion',
  techToolsUsed: 'Technological Tools Used',
  techToolsAreas: 'Tool Deployment Areas',
  restatement: 'Restatement',
  restatementDesc: 'Restatement Description',
  remarks: 'Remarks',
  reviewerName: 'Reviewer',
};

const fmtChangeValue = withCoverageFmt((field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDate(s) : s;
});

const fsFileBadge = (f) => (f.docType === 'fs'
  ? ['Signed Financial Statements', 'bg-blue-50 text-blue-700']
  : f.docType === 'qc35'
    ? ['QC35 Form', 'bg-blue-50 text-blue-700']
    : ['Supporting', 'bg-slate-100 text-slate-600']);

const WorkflowFsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [request, setRequest] = useState(null);
  const [currentEngagement, setCurrentEngagement] = useState(null);
  const [engagementAttachments, setEngagementAttachments] = useState([]);
  const [aiReview, setAiReview] = useState(null);
  const [batchSiblings, setBatchSiblings] = useState([]);
  const [review, setReview] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canViewEngagement, setCanViewEngagement] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [engagementLock, setEngagementLock] = useState(null);
  const [archivalDeadlinePreview, setArchivalDeadlinePreview] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/fs/${id}`),
        fetchJSON(`/api/workflow/fs/${id}/events`),
      ]);
      setRequest(detail.request);
      setCurrentEngagement(detail.currentEngagement);
      setEngagementAttachments(detail.engagementAttachments || []);
      setAiReview(detail.aiReview || null);
      setBatchSiblings(detail.batchSiblings || []);
      setReview(detail.review || null);
      setCanReview(detail.canReview);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setCanViewEngagement(detail.canViewEngagement === true);
      setEngagementLock(detail.engagementLock || null);
      setArchivalDeadlinePreview(detail.archivalDeadlinePreview || null);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/workflow/fs/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const parts = [data.request?.status === 'approved'
        ? 'The submission is approved.'
        : 'Your approval is recorded.'];
      if (data.stamp?.stamped?.length) {
        parts.push(`Sign-off date ${data.request.signingDate} recorded on the covered engagement(s).`);
        if (data.stamp?.archivalDeadline) parts.push(`Archival deadline is ${data.stamp.archivalDeadline} (sign-off + ${daysBetween(data.request.signingDate, data.stamp.archivalDeadline)} days).`);
      }
      if (data.stamp?.failed?.length) parts.push('Some sign-off stamps failed — update those engagements in Management.');
      setNotice(parts.join(' '));
      await load();
    } catch (e) { setError(e.message); throw e; }
    finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/fs/${id}/resubmit`, { method: 'POST' });
      setNotice('Submission re-submitted — reviewers have been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  // A rejected submission sits at 'awaiting_info' — the member can save edits
  // (Modify) and/or press Re-submit to re-enter the review queue.
  const hasAwaitingInfo = request && request.status === 'awaiting_info';

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/fs"
      backLabel="Signed Financial Statements"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel={FS_TYPE_LABELS[request.requestType]}
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            lock={engagementLock}
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && request.status === 'requested'
              ? () => (request.signingDate ? setApproving(true) : approve().catch(() => {}))
              : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify && request.status !== 'approved' ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            <div>
              <p className={detailLabel}>Signing Date</p>
              <p className={detailValue}>{fmtDate(request.signingDate)}</p>
              {request.status === 'approved' && request.signingDate && (
                <p className="text-xs text-green-600 mt-1">Recorded as engagement sign-off</p>
              )}
              {/* 簽核日住在狀態卡上（核准時它會寫進 engagement），不是詳情格線裡的
                  一格——所以它的評論入口掛在這裡。 */}
              <FieldCommentAnchor field="signingDate" label="Signing Date" className="mt-1" compact />
            </div>
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          <ApprovalsCard
            review={review}
            approvals={request.approvals || []}
            status={request.status}
            aiApproved={request.aiApproved}
            autoApproved={request.autoApproved}
          />

          {/* AI 自動核准的免審證據：三條件判定＋當時送審的兩張頁面圖。 */}
          {request.aiApproved && aiReview && (
            <SectionCard title="AI Review" count={aiReview.passed ? 'Passed' : 'Not passed'}>
              <FsAiConditionsList conditions={aiReview.conditions} />
              <p className="mt-3 text-xs text-slate-500">
                Reviewed by {aiReview.model} on {formatDateTime(aiReview.createdAt)}.
              </p>
              <FsAiPageThumbs images={[
                aiReview.hasFirstPageImage && {
                  caption: 'Page 1 as reviewed',
                  url: `${API_BASE_URL}/api/workflow/fs/ai-reviews/${aiReview.id}/image/first`,
                },
                aiReview.hasPageImage && {
                  caption: 'Signing-date page as reviewed',
                  url: `${API_BASE_URL}/api/workflow/fs/ai-reviews/${aiReview.id}/image/page`,
                },
              ]} />
            </SectionCard>
          )}

          {/* What is being reviewed comes FIRST: the submission's own answers
              and its signed document sit above the engagement's reference
              snapshot, so the reviewer sees the thing to approve before the
              background material. */}
          <SectionCard title="Submission Details">
            <DetailGrid>
              <DetailItem label={`Engagement(s) Covered (${(request.engagements || []).length || 1})`}>
                {(request.engagements || []).length > 0
                  ? <EngagementNameList items={request.engagements} keyOf={(e, i) => e.id || i} />
                  : <EngagementName name={request.engagementName} serviceDescription={request.serviceDescription} />}
              </DetailItem>
              <DetailItem label="Type">{FS_TYPE_LABELS[request.requestType]}</DetailItem>
              {/* 同一頁一起送出的其他公司——各自一張單、各自審核。 */}
              {batchSiblings.length > 0 && (
                <DetailItem label="Submitted Together With">
                  <BatchSiblings siblings={batchSiblings} current={currentEngagement} basePath="/workflow/fs" />
                </DetailItem>
              )}
              <DetailItem label="Audit Opinion" field="auditOpinion">{request.auditOpinion || '—'}</DetailItem>
              <DetailItem label="Technological Tools Used" field="techToolsUsed">{yesNoDash(request.techToolsUsed)}</DetailItem>
              <DetailItem label="Tool Deployment Areas" field="techToolsAreas">{request.techToolsAreas || '—'}</DetailItem>
              <DetailItem label="Restatement" field="restatement">{yesNoDash(request.restatement)}</DetailItem>
              <DetailItem label="Restatement Description" field="restatementDesc">{request.restatementDesc || '—'}</DetailItem>
              <DetailItem label="Independence Declaration">{request.independenceDeclared ? 'Confirmed by requester' : '—'}</DetailItem>
              {/* 這條流程沒有審核關，所以只有改制前點過名的舊單才有這一格。 */}
              {request.reviewerName && (
                <DetailItem label="Reviewer" field="reviewerName">
                  <NowName value={request.reviewerName} now={request.reviewerNameNow} />
                </DetailItem>
              )}
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Attachments">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/fs/${request.id}/files/${f.id}`}
              badgeFor={fsFileBadge}
            />
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          {/* Linked engagement: full current snapshot + its own attachments —
              reference material, so it sits below the submission itself. */}
          {currentEngagement && (
            <SectionCard
              title="Engagement Information"
              actions={(() => {
                // Editors go to Manage Data; everyone else to their own
                // read-only copy — otherwise this link dead-ends on the access
                // wall for a reader without the key.
                // A named reviewer off the team has no engagement page at
                // all — no link rather than one that opens on an access wall.
                const canEdit = hasPermission('engagements:edit');
                const href = canEdit || canViewEngagement ? engagementHref(currentEngagement, canEdit) : null;
                return href ? (
                  <RefLink to={href} className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">
                    {canEdit ? 'Open in Management' : 'Open in My Records'} <ArrowRight className="w-4 h-4" />
                  </RefLink>
                ) : null;
              })()}
            >
              <DetailGrid>
                <div className="sm:col-span-2">
                  <DetailItem label="Engagement"><EngagementName name={currentEngagement.engagementName} now={currentEngagement.engagementNameNow} serviceDescription={currentEngagement.serviceDescription} /></DetailItem>
                </div>
                <DetailItem label="Entity Name"><NowName value={currentEngagement.entityName} now={currentEngagement.entityNameNow} /></DetailItem>
                <DetailItem label="UEN"><NowName value={currentEngagement.uen} now={currentEngagement.uenNow} /></DetailItem>
                <DetailItem label="Client Group"><NowName value={currentEngagement.clientGroup} now={currentEngagement.clientGroupNow} /></DetailItem>
                <DetailItem label="Year End (FYE)">{fmtDate(currentEngagement.yearEnd)}</DetailItem>
                <DetailItem label="Status">{currentEngagement.status || '—'}</DetailItem>
                <DetailItem label="Service Provided">{currentEngagement.serviceProvided || '—'}</DetailItem>
                <DetailItem label="Partner">{currentEngagement.partner || '—'}</DetailItem>
                <DetailItem label="Director">{currentEngagement.director || '—'}</DetailItem>
                <DetailItem label="Manager">{currentEngagement.manager || '—'}</DetailItem>
                <DetailItem label="EQR">{currentEngagement.eqr || '—'}</DetailItem>
                <DetailItem label="CaseWare">{currentEngagement.casewareOpened ? 'Activated' : 'Not activated'}</DetailItem>
                <DetailItem label="Charge Code">{currentEngagement.chargeCodeAssigned ? 'Activated' : 'Not activated'}</DetailItem>
                <div className="sm:col-span-2">
                  <DetailItem label="Team Members">
                    <StaffNames list={currentEngagement.teamMembers} />
                  </DetailItem>
                </div>
              </DetailGrid>

              {/* The engagement's own documents (Setup QC6/7 + Signed Financial Statements) */}
              <div className="mt-5 pt-4 border-t border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Engagement Attachments</h3>
                <EngagementAttachments
                  attachments={engagementAttachments}
                  downloadHref={(f) => `${API_BASE_URL}/api/workflow/fs/${request.id}/engagement-files/${f.source}/${f.fileId}`}
                />
              </div>
            </SectionCard>
          )}

          <HistoryCard
            events={events}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          {approving && (
            <FsArchivalDeadlineModal
              signingDate={request.signingDate}
              deadline={archivalDeadlinePreview}
              onClose={() => setApproving(false)}
              onConfirm={async () => { await approve(); setApproving(false); }}
            />
          )}
          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/fs/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <FsModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the submission back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the submission and its attachments. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/fs/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/fs');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Approval confirmation at FS approval. The archival deadline is derived from
// the record's signing date; the server computes it (archivalDeadlinePreview,
// the same rule the approval stamps) and the reviewer is informed of it here
// but cannot change it.
const daysBetween = (from, to) =>
  Math.round((Date.parse(String(to).slice(0, 10)) - Date.parse(String(from).slice(0, 10))) / 86400000);

const FsArchivalDeadlineModal = ({ signingDate, deadline, onClose, onConfirm }) => {
  const days = deadline ? daysBetween(signingDate, deadline) : null;
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await onConfirm(); }
    catch (e) { setError(e.message); setSaving(false); }
  };
  return (
    <FormModal
      title="Approve — Archival Deadline"
      size="md"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnApprove}>
            {saving ? 'Approving…' : 'Approve'}
          </button>
        </>
      )}
    >
      <p className="text-sm text-slate-700">
        Approving records the sign-off date <span className="font-medium">{fmtDate(signingDate)}</span> on
        the engagement. The archival deadline is sign-off + {days} days:
      </p>
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800">
        Archival deadline: <span className="font-semibold">{fmtDate(deadline)}</span>
        <span className="text-slate-500"> — the engagement file must be archived on or before this date.</span>
      </div>
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error}</span>
        </div>
      )}
    </FormModal>
  );
};

const FsModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [signingDate, setSigningDate] = useState(request.signingDate || '');
  const [auditOpinion, setAuditOpinion] = useState(request.auditOpinion || '');
  const [techToolsUsed, setTechToolsUsed] = useState(request.techToolsUsed === true ? 'yes' : request.techToolsUsed === false ? 'no' : '');
  const [techToolsAreas, setTechToolsAreas] = useState(request.techToolsAreas || '');
  const [restatement, setRestatement] = useState(request.restatement === true ? 'yes' : request.restatement === false ? 'no' : '');
  const [restatementDesc, setRestatementDesc] = useState(request.restatementDesc || '');
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [filesFs, setFilesFs] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Auto-detect works on a freshly picked replacement PDF (existing uploads
  // live on the server; the scan is client-side only).
  const detect = useFsAutoDetect({
    files: filesFs,
    onDetected: setSigningDate,
    disabled: saving,
  });

  // The New form's required check, in Modal terms: the coverage is not
  // editable here, signed financial statements files already on the request (and not marked for
  // removal) count toward the upload requirement, and the independence
  // declaration recorded at submission is never re-asked. Legacy QC35
  // submissions carry neither a signing date nor a signed financial statements document, so the
  // check does not apply to them.
  const keptFsFiles = (request.files || [])
    .filter(f => f.docType === 'fs' && !removeIds.includes(f.id));
  const keptFsCount = keptFsFiles.length;

  // Everything still empty, keyed to the control it belongs to — after the
  // first Save the modal marks those controls and lists them in the banner.
  const gaps = useMemo(() => (request.requestType === 'qc35'
    ? []
    : [
      ...fsRequiredGaps({ signingDate, auditOpinion }, filesFs, { existingFsFiles: keptFsCount }),
    ]
  ), [request.requestType, signingDate, auditOpinion, filesFs, keptFsCount]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      if (signingDate) form.set('signingDate', signingDate);
      if (auditOpinion) form.set('auditOpinion', auditOpinion);
      if (techToolsUsed !== '') form.set('techToolsUsed', String(techToolsUsed === 'yes'));
      form.set('techToolsAreas', techToolsAreas);
      if (restatement !== '') form.set('restatement', String(restatement === 'yes'));
      form.set('restatementDesc', restatementDesc);
      form.set('remarks', remarks);
      filesFs.forEach(f => form.append('filesFs', f));
      appendTo(form);
      await fetchJSON(`/api/workflow/fs/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  // Which required document kinds this save would leave the submission without
  // — mirrors the server rule (workflow.routes.js PATCH /fs): "had one before"
  // must still mean "has one after", counting what is being uploaded now.
  const missingAfterRemoval = (() => {
    if (removeIds.length === 0) return [];
    // Legacy 'both'/'qc35' rows still guard their QC35 document — the modal no
    // longer uploads one, so removing it always needs the removal undone.
    const kinds = request.requestType === 'both' ? ['fs', 'qc35'] : [request.requestType];
    const kept = (request.files || []).filter(f => !removeIds.includes(f.id));
    const incoming = { fs: filesFs.length > 0 };
    return kinds
      .filter(k => (request.files || []).some(f => f.docType === k))
      .filter(k => !kept.some(f => f.docType === k) && !incoming[k])
      .map(k => (k === 'fs' ? 'The Signed Financial Statements upload' : 'The QC35 Form upload'));
  })();

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving || detect.busy} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ReturnCommentsBanner events={events} />
        {request.requestType !== 'qc35' && (
          <>
            <FsSigningDateField value={signingDate} onChange={setSigningDate} detect={detect} />
            <FsAuditOpinionField value={auditOpinion} onChange={setAuditOpinion} />
          </>
        )}
        <FsAuditQuestionFields
          techToolsUsed={techToolsUsed} setTechToolsUsed={setTechToolsUsed}
          techToolsAreas={techToolsAreas} setTechToolsAreas={setTechToolsAreas}
          restatement={restatement} setRestatement={setRestatement}
          restatementDesc={restatementDesc} setRestatementDesc={setRestatementDesc}
        />
        <div>
          <EditableAttachments
            files={request.files || []}
            removeIds={removeIds}
            onToggle={toggle}
            downloadHref={(f) => `${API_BASE_URL}/api/workflow/fs/${request.id}/files/${f.id}`}
            badgeFor={fsFileBadge}
            label="Attachments"
          />
          <FieldCommentInline field="files" />
          {(request.files || []).length === 0 && (
            <p className="mb-2 text-xs text-slate-500">No files attached yet.</p>
          )}
          {/* The server refuses to leave a submission without a document it
              already carries — say so here rather than on the round-trip. */}
          {missingAfterRemoval.length > 0 && (
            <p className="mt-2 mb-1 text-xs text-red-600">
              {missingAfterRemoval.join(' and ')} {missingAfterRemoval.length > 1 ? 'are' : 'is'} required
              {' '}— attach a replacement before saving.
            </p>
          )}
          {/* Signed Financial Statements uploads only — new submissions are fs-only, and legacy
              QC35 rows never carried one. */}
          {request.requestType !== 'qc35' && (
            <>
              <label className="block text-xs font-medium text-slate-500 mt-2 mb-1.5">Add More Files</label>
              <FilePicker
                multiple
                kind="docsOnly"
                disabled={detect.busy}
                onChange={e => setFilesFs(Array.from(e.target.files || []))}
              />
              {/* Not a Field — the mark for "no signed financial statements left on the request"
                  belongs right under the picker that fixes it. */}
              <FieldInvalidInline field="filesFs" text="Required — attach the signed financial statements" />
            </>
          )}
        </div>
        <FsRemarksField value={remarks} onChange={setRemarks} />
        {(error || validation.error) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <span>{error || validation.error}</span>
          </div>
        )}
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowFsDetail;
